"use client";

/**
 * P3-09 · Unified wishlist store. Guests use localStorage; a signed-in shopper
 * uses the account-backed `WishlistItem` table via tRPC, toggled optimistically
 * with rollback. On sign-in the guest's local list merges into the account once
 * (deduped server-side), then the local copy is cleared — the list follows the
 * shopper across devices. The consumer sees one `{ has, toggle }` interface
 * regardless of auth.
 *
 * P14-10 · `useCompare` / `useSaveForLater` lived here for three phases,
 * writing localStorage keys that nothing ever read. They are gone rather than
 * promoted: neither had a route to show its result on.
 */
import * as React from "react";

import { api } from "~/trpc/react";
import { track as capture } from "~/lib/analytics/client";
import {
  clearGuestSet,
  readGuestSet,
  useLocalToggleSet,
} from "~/lib/wishlist/use-local-toggle-set";

export const WISHLIST_KEY = "vxolt.wishlist";

/**
 * Whether the guest → account merge has already run in this page load. Shared
 * across every `useWishlist` instance on purpose — see the effect below.
 */
let guestMigrationDone = false;

/** Test seam: the module flag outlives a component tree, so tests must clear it. */
export function __resetGuestMigration(): void {
  guestMigrationDone = false;
}

export interface WishlistStore {
  has: (productId: string) => boolean;
  /** Flip the product; returns the new saved state. */
  toggle: (productId: string) => boolean;
}

export function useWishlist({
  signedIn,
  enabled = true,
}: {
  signedIn: boolean;
  enabled?: boolean;
}): WishlistStore {
  // Guest store — active only when signed out (and the feature is enabled).
  const guest = useLocalToggleSet(WISHLIST_KEY, enabled && !signedIn);

  const utils = api.useUtils();
  const list = api.wishlist.list.useQuery(undefined, {
    enabled: signedIn && enabled,
    staleTime: 30_000,
  });

  const toggleM = api.wishlist.toggle.useMutation({
    // Optimistic: reflect the toggle immediately, roll back on server refusal.
    onMutate: async ({ productId }) => {
      await utils.wishlist.list.cancel();
      const prev = utils.wishlist.list.getData();
      utils.wishlist.list.setData(undefined, (old) => {
        const cur = old ?? [];
        return cur.includes(productId)
          ? cur.filter((id) => id !== productId)
          : [productId, ...cur];
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) utils.wishlist.list.setData(undefined, context.prev);
    },
    onSettled: () => void utils.wishlist.list.invalidate(),
  });

  const importM = api.wishlist.import.useMutation();

  // One-shot guest → account migration on sign-in.
  //
  // P14-10 · The guard is module-level, not a per-instance ref, and that
  // changed with this ticket. While only the PDP mounted this hook, one
  // instance meant one migration; the product-card heart now mounts it once
  // per card, so a shopper who signs in and lands on a 24-card grid would have
  // fired 24 identical `import` mutations. "One shot" means once per page
  // load, which is what a module-level flag actually says.
  React.useEffect(() => {
    if (!signedIn || !enabled || guestMigrationDone) return;
    guestMigrationDone = true;
    const productIds = readGuestSet(WISHLIST_KEY);
    if (productIds.length === 0) return;
    importM.mutate(
      { productIds },
      {
        onSuccess: () => {
          clearGuestSet(WISHLIST_KEY);
          void utils.wishlist.list.invalidate();
        },
      },
    );
    // importM/utils are stable; excluded to keep this a one-shot effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, enabled]);

  if (!signedIn) {
    return {
      has: guest.has,
      toggle: (id) => {
        const next = guest.toggle(id);
        // P9-01 · Funnel wishlist-add signal (only on add, not remove).
        if (next) capture("WISHLIST_ADD", { productId: id });
        return next;
      },
    };
  }

  const saved = new Set(list.data ?? []);
  return {
    has: (id) => saved.has(id),
    toggle: (id) => {
      const next = !saved.has(id);
      toggleM.mutate({ productId: id });
      if (next) capture("WISHLIST_ADD", { productId: id });
      return next;
    },
  };
}

/**
 * P9-12 · Live wishlist count for the mobile bottom-nav badge. Mirrors
 * `useWishlist`'s two sources (the account list for signed-in shoppers, the
 * guest localStorage set otherwise) so the badge stays in lockstep with the
 * wishlist heart everywhere else. Hooks are called unconditionally; `enabled`
 * gates the data, not the calls.
 */
export function useWishlistCount({
  signedIn,
  enabled = true,
}: {
  signedIn: boolean;
  enabled?: boolean;
}): number {
  const guest = useLocalToggleSet(WISHLIST_KEY, enabled && !signedIn);
  const list = api.wishlist.list.useQuery(undefined, {
    enabled: signedIn && enabled,
    staleTime: 30_000,
  });
  if (!enabled) return 0;
  return signedIn ? (list.data?.length ?? 0) : guest.items.length;
}


